import { useState } from 'react';
import type { WizardPreview } from '../../services/schedulerWizardApi';
import type { WizardPrerequisites } from '../../services/schedulerWizardPrerequisites';
import { formatDate, formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import { wizardDutyLabel } from './SchedulerPatternPreview';

type Interval = WizardPreview['timeline'][number];
const DAY_MS = 86400000;

export function scheduleTimezoneLabel(timezone: string, utc: string) {
  const date = new Date(utc);
  const name = new Intl.DateTimeFormat('en-AU', { timeZone: timezone, timeZoneName: 'short' })
    .formatToParts(date).find(part => part.type === 'timeZoneName')?.value;
  const offset = new Intl.DateTimeFormat('en-AU', { timeZone: timezone, timeZoneName: 'longOffset' })
    .formatToParts(date).find(part => part.type === 'timeZoneName')?.value?.replace('GMT', 'UTC');
  return `${timezone.split('/').pop()?.replace(/_/g, ' ')} - ${name} - ${offset} (${timezone})`;
}

const localTime = (utc: string | number, timezone: string) => formatDateTime(utc,
  { dateStyle: 'medium', timeStyle: 'short' }, { timezone, hourCycle: 'h23' });

export function mergeRosterIntervals(entries: Interval[]): Interval[] {
  const sorted = [...entries].sort((a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc));
  const merged: Interval[] = [];
  for (const entry of sorted) {
    const previous = merged[merged.length - 1];
    if (previous && Date.parse(previous.endUtc) === Date.parse(entry.startUtc) &&
      previous.laneId === entry.laneId && previous.role === entry.role && previous.kind === entry.kind &&
      previous.groupId === entry.groupId && previous.assigneeId === entry.assigneeId &&
      previous.isRequired === entry.isRequired) {
      previous.endUtc = entry.endUtc;
      previous.endLocal = entry.endLocal;
    } else merged.push({ ...entry });
  }
  return merged;
}

export function SchedulerRosterPreview({ preview, snapshot, roleFilter }: {
  preview: WizardPreview; snapshot: WizardPrerequisites | null; roleFilter: string;
}) {
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Interval | null>(null);
  const preferences = useDateTimePreferences();
  const timezone = preview.normalizedDraft.timeZoneId;
  const axisDate = (utc: number) => {
    const settings = { timezone, dateTimeLocale: preferences.locale };
    return `${formatDate(utc, { weekday: 'long' }, settings)} (${formatDate(utc,
      { day: '2-digit', month: '2-digit', year: '2-digit' }, settings)})`;
  };
  const rangeStart = Date.parse(preview.previewRange.startUtc);
  const rangeEnd = Date.parse(preview.previewRange.endUtc);
  const pages = Math.max(1, Math.ceil((rangeEnd - rangeStart) / (days * DAY_MS)));
  const currentPage = Math.min(page, pages - 1);
  const start = rangeStart + currentPage * days * DAY_MS;
  const end = Math.min(rangeEnd, start + days * DAY_MS);
  const entries = preview.timeline.filter(entry => roleFilter === 'All' || entry.role === roleFilter);
  const rows = [...new Set(entries.map(entry => `${entry.role}/${entry.laneId || 'coverage'}`))].map(key => {
    const intervals = entries.filter(entry => `${entry.role}/${entry.laneId || 'coverage'}` === key);
    const lane = preview.normalizedDraft.lanes.find(item => item.id === intervals[0].laneId);
    const label = lane ? wizardDutyLabel(lane, preview.normalizedDraft.lanes.filter(item => item.role === lane.role).indexOf(lane))
      : `${intervals[0].role} service coverage`;
    const group = snapshot?.groups.find(item => item.groupId === lane?.groupId)?.name;
    const tracks: Interval[][] = [];
    for (const interval of mergeRosterIntervals(intervals).filter(item => Date.parse(item.startUtc) < end && Date.parse(item.endUtc) > start)) {
      const track = tracks.find(items => Date.parse(items[items.length - 1].endUtc) <= Date.parse(interval.startUtc));
      if (track) track.push(interval);
      else tracks.push([interval]);
    }
    return { key, label, group, tracks };
  });
  const users = [...new Set(preview.timeline.map(entry => entry.assigneeId).filter((id): id is string => !!id))].sort();
  const visibleOwners = [...new Map(entries.filter(entry => entry.assigneeId &&
    Date.parse(entry.startUtc) < end && Date.parse(entry.endUtc) > start).map(entry => [entry.assigneeId, entry])).values()]
    .sort((a, b) => users.indexOf(a.assigneeId!) - users.indexOf(b.assigneeId!));
  const ownerColor = (id: string) => `hsl(${users.indexOf(id) * 137.508 % 360} 60% 45%)`;
  const description = (entry: Interval) => {
    const group = snapshot?.groups.find(item => item.groupId === entry.groupId)?.name || 'No group';
    return `${entry.assigneeName || entry.kind} | ${group} | ${entry.kind} | ${localTime(entry.startUtc, timezone)} to ${localTime(entry.endUtc, timezone)}`;
  };
  return <section aria-label="Pre-publication roster" className="wizard-roster-preview">
    <h3>Visual schedule preview - indicative roster assignments</h3>
    <p>{scheduleTimezoneLabel(timezone, new Date(start).toISOString())}</p>
    <p>{localTime(start, timezone)} to {localTime(end, timezone)} (end-exclusive).
      Bars use server-calculated owners, not just pattern bands. Nothing is published.
      Select a bar for exact times; use Back or the earlier steps to modify staffing, then preview again.</p>
    <div className="wizard-row">
      <label>Visual preview span<select value={days} onChange={event => { setDays(Number(event.target.value)); setPage(0); setSelected(null); }}>
        <option value={1}>1 day</option><option value={7}>7 days</option><option value={14}>14 days</option>
      </select></label>
      <button type="button" disabled={currentPage === 0} onClick={() => { setPage(currentPage - 1); setSelected(null); }}>Previous roster period</button>
      <span>Period {currentPage + 1} of {pages}</span>
      <button type="button" disabled={currentPage + 1 >= pages} onClick={() => { setPage(currentPage + 1); setSelected(null); }}>Next roster period</button>
    </div>
    <p>Axis labels are schedule-local; spacing represents elapsed time. DST can change local tick times and UTC offsets.</p>
    <ul className="wizard-roster-legend" aria-label="Indicative responder key">
      {visibleOwners.map(entry => <li key={entry.assigneeId} style={{ borderLeftColor: ownerColor(entry.assigneeId!) }}>
        {users.indexOf(entry.assigneeId!) + 1}. {entry.assigneeName || entry.assigneeId}
      </li>)}
    </ul>
    <div className="wizard-pattern-scroll">
      <div className="wizard-roster-canvas" style={{ minWidth: `${220 + Math.ceil((end - start) / DAY_MS) * 130}px` }}>
        <div className="wizard-roster-axis">
          <strong>Duty / resource group</strong>
          <div>{Array.from({ length: Math.ceil((end - start) / DAY_MS) }, (_, index) =>
            <span key={index} title={`${localTime(start + index * DAY_MS, timezone)} | ${scheduleTimezoneLabel(timezone, new Date(start + index * DAY_MS).toISOString())}`}
              style={{ left: `${index * DAY_MS / (end - start) * 100}%` }}>{axisDate(start + index * DAY_MS)}</span>)}</div>
        </div>
        {rows.map(row => <div className="wizard-roster-visual-row" key={row.key}>
          <div><strong>{row.label}</strong><br />{row.group}</div>
          <div>
            {!row.tracks.length && <p>No duty in this period.</p>}
            {row.tracks.map((track, index) => <div className="wizard-roster-track" key={index}
              style={{ backgroundSize: `${DAY_MS / (end - start) * 100}% 100%` }}>
              {track.map(entry => <button type="button" key={`${entry.startUtc}-${entry.endUtc}-${entry.assigneeId}`}
                className={`wizard-roster-assignment ${entry.assigneeId ? '' : 'wizard-roster-unassigned'}`}
                title={description(entry)} aria-label={`${row.label}: ${description(entry)}`}
                onClick={() => setSelected(entry)}
                style={{
                  left: `${(Math.max(start, Date.parse(entry.startUtc)) - start) / (end - start) * 100}%`,
                  width: `${(Math.min(end, Date.parse(entry.endUtc)) - Math.max(start, Date.parse(entry.startUtc))) / (end - start) * 100}%`,
                  borderTopColor: entry.assigneeId ? ownerColor(entry.assigneeId) : undefined,
                }}>{entry.assigneeId ? `${users.indexOf(entry.assigneeId) + 1}. ${entry.assigneeName || entry.assigneeId}` : entry.kind}</button>)}
            </div>)}
          </div>
        </div>)}
      </div>
    </div>
    {!rows.length && <p>No server intervals for this duty role.</p>}
    {selected && (roleFilter === 'All' || selected.role === roleFilter) && <div className="wizard-summary" role="status">
      <strong>{description(selected)}</strong><br />
      {scheduleTimezoneLabel(timezone, selected.startUtc)} to {scheduleTimezoneLabel(timezone, selected.endUtc)}<br />
      UTC: {selected.startUtc} to {selected.endUtc}
      {(() => {
        const member = selected.groupId ? snapshot?.members[selected.groupId]?.find(item => item.userId === selected.assigneeId) : undefined;
        return member?.timezone ? <p>Responder-local ({member.timezone}): {localTime(selected.startUtc, member.timezone)} to {localTime(selected.endUtc, member.timezone)}</p> : null;
      })()}
    </div>}
  </section>;
}
